import { getConsent, subscribeToConsent, ConsentCategories } from "../consent/index.js";
import { gtag } from "./gtag.js";
import { pushConsentEvent } from "./consentEvent.js";
import {
  ConsentSignalState,
  GoogleConsentSignals,
  GTAG_CONSENT_COMMAND,
  GTAG_CONSENT_UPDATE,
  DEFAULT_CONSENT_MODE_SIGNALS,
} from "./constants.js";

/**
 * Google Consent Mode v2 — the bridge between the Consent Foundation and Google.
 *
 * Division of labour, in the order it happens on a page load:
 *
 *   1. index.html (inline, before GTM)  establishes the DEFAULT: everything
 *      measurable denied. This has to be inline because it must precede the GTM
 *      loader, which runs long before any bundled module.
 *   2. GTM loads exactly as it always has. This module never blocks, delays, or
 *      conditionally injects it — Consent Mode restricts what Google DOES with
 *      what it receives; it is not an on/off switch for the container.
 *   3. `initConsentMode()` (from the app entry point) reads the stored answer
 *      and sends an UPDATE, then subscribes so every later change sends another.
 *   4. Each update is immediately followed by a `consent_updated` dataLayer event
 *      (`consentEvent.js`). Google reads the gtag command; GTM's trigger engine
 *      cannot, so that event is the only way a container can react at the moment
 *      consent changes — e.g. firing a Meta Pixel that was blocked on page load.
 *      The two pushes are one atomic step: same guard, fixed order, never one
 *      without the other.
 *
 * What this module deliberately does NOT do: gate `window.dataLayer` events.
 * Analytics events keep firing exactly as they did before Phase 7.0B, and
 * Google's own consent state decides what may be stored or reported. Those are
 * two different mechanisms and conflating them is how sites end up double-gating
 * and silently losing consented traffic — see docs/analytics/google-consent-mode.md.
 */

/**
 * The last signal set actually sent to Google, serialized, or `null` before the
 * first update. This is what makes repeated updates idempotent: re-sending an
 * identical state is a no-op for Google but adds noise to the dataLayer and to
 * anyone debugging with Tag Assistant, and a re-render loop calling `sync` on
 * every pass would quietly fill the array.
 *
 * Module-scoped so it survives component mount/unmount, the same reasoning as
 * the listener registry in the Consent Foundation.
 *
 * @type {string | null}
 */
let lastPushedSignals = null;

/**
 * Unsubscribe handle for the foundation subscription, or `null` when this
 * module has not been initialized. Doubles as the "already initialized" flag so
 * `initConsentMode()` can be called more than once (React StrictMode, HMR,
 * an entry point that runs twice) without stacking duplicate listeners.
 *
 * @type {(() => void) | null}
 */
let unsubscribeFromConsent = null;

/**
 * The default signal set, as a fresh mutable copy.
 *
 * `DEFAULT_CONSENT_MODE_SIGNALS` is frozen; handing out the frozen reference
 * would let a caller alias a module constant. Same discipline as
 * `getDefaultConsent()` in the foundation.
 *
 * @returns {import("./types.js").ConsentModeSignals}
 */
export function getDefaultConsentModeSignals() {
  return { ...DEFAULT_CONSENT_MODE_SIGNALS };
}

/**
 * Translate the visitor's consent preferences into Google's six signals.
 *
 * The mapping, and why:
 *   - `analytics_storage`  ← the ANALYTICS category. GA4's own storage.
 *   - `ad_storage`, `ad_user_data`, `ad_personalization` ← the MARKETING
 *     category. Our banner asks one marketing question; Google splits the answer
 *     three ways (cookie storage, sending user data, personalizing ads). One
 *     "yes" therefore grants all three, and one "no" denies all three. Deriving
 *     them from a single category rather than storing three is what keeps the
 *     banner honest: we never claim a granularity the visitor was not offered.
 *   - `functionality_storage`, `security_storage` ← always granted. Strictly
 *     necessary storage does not require consent (Greek Law 3471/2006, Art.
 *     4(5)); this is the `essential` category, which is always true.
 *
 * Deny-by-default throughout: only an explicit `true` grants, so a missing,
 * partial, or unrecognized preferences object yields the same all-denied set as
 * a visitor who has not answered.
 *
 * @param {import("../consent/types.js").ConsentPreferences} preferences
 * @returns {import("./types.js").ConsentModeSignals}
 */
export function mapConsentToSignals(preferences) {
  const analytics = preferences?.[ConsentCategories.ANALYTICS] === true;
  const marketing = preferences?.[ConsentCategories.MARKETING] === true;

  const state = (granted) => (granted ? ConsentSignalState.GRANTED : ConsentSignalState.DENIED);

  return {
    [GoogleConsentSignals.AD_STORAGE]: state(marketing),
    [GoogleConsentSignals.AD_USER_DATA]: state(marketing),
    [GoogleConsentSignals.AD_PERSONALIZATION]: state(marketing),
    [GoogleConsentSignals.ANALYTICS_STORAGE]: state(analytics),
    [GoogleConsentSignals.FUNCTIONALITY_STORAGE]: ConsentSignalState.GRANTED,
    [GoogleConsentSignals.SECURITY_STORAGE]: ConsentSignalState.GRANTED,
  };
}

/**
 * Send a signal set to Google, unless it is identical to the last one sent, and
 * announce the change to GTM.
 *
 * Both pushes sit INSIDE the idempotency guard, so a state change is always
 * exactly one command and exactly one announcement — never a command GTM is not
 * told about, and never an announcement of a change that did not happen.
 *
 * @param {import("./types.js").ConsentModeSignals} signals
 * @returns {boolean} `true` if an update was actually pushed.
 */
function pushSignals(signals) {
  if (typeof window === "undefined") return false;

  const serialized = JSON.stringify(signals);
  if (serialized === lastPushedSignals) return false;

  gtag(GTAG_CONSENT_COMMAND, GTAG_CONSENT_UPDATE, signals);
  // AFTER the command, never before. GTM processes the dataLayer in order, and a
  // trigger firing on this event is evaluated against the consent state in
  // effect at that instant. Announce first and every consent-gated tag is
  // re-checked while the OLD state still stands — blocked, with nothing anywhere
  // to say why. The order of these two lines IS the feature.
  pushConsentEvent(signals);
  lastPushedSignals = serialized;
  return true;
}

/**
 * Send the CURRENT consent state to Google.
 *
 * Reads the effective state from the foundation — an unanswered visitor reads as
 * deny-by-default, never as `null` — so this is always safe to call.
 *
 * The first call always pushes, even when the result equals the default already
 * declared inline. That is intentional: it makes the update independent of
 * whether the inline snippet ran, and it releases Google's `wait_for_update`
 * timer immediately instead of making every visitor wait it out.
 *
 * @param {import("../consent/types.js").ConsentPreferences} [preferences]
 *   Optional explicit preferences — used by the subscription, which already
 *   holds the freshly written record and should not race a cookie re-read.
 *   Omit to read the current state.
 * @returns {import("./types.js").ConsentModeSignals | null}
 *   The signals pushed, or `null` if nothing was sent (unchanged state, or a
 *   non-browser environment).
 */
export function syncConsentMode(preferences) {
  if (typeof window === "undefined") return null;

  const source = preferences || getConsent().preferences;
  const signals = mapConsentToSignals(source);
  return pushSignals(signals) ? signals : null;
}

/**
 * Wire Consent Mode up: send the current state once, then keep Google in step
 * with every later consent change.
 *
 * Call this as early as the entry point allows — before React renders, next to
 * `initSentry()`. Idempotent: calling it again returns the existing
 * subscription rather than adding a second listener.
 *
 * @returns {() => void} Unsubscribe. Rarely needed in an app (the subscription
 *   should live as long as the page) but it keeps the contract honest and lets
 *   tests tear down.
 */
export function initConsentMode() {
  if (unsubscribeFromConsent) return unsubscribeFromConsent;

  syncConsentMode();

  const unsubscribe = subscribeToConsent((state) => {
    syncConsentMode(state?.preferences);
  });

  unsubscribeFromConsent = () => {
    unsubscribe();
    unsubscribeFromConsent = null;
  };

  return unsubscribeFromConsent;
}

/**
 * Drop the subscription and forget what was last sent, returning the module to
 * its pre-`initConsentMode()` state.
 *
 * Exists for tests, which need each case to start from a clean slate — the
 * idempotency guard is module-scoped and would otherwise leak between them.
 * Exported from the module but NOT from the public barrel, so it cannot be
 * reached from application code by accident.
 *
 * @returns {void}
 */
export function resetConsentModeForTests() {
  if (unsubscribeFromConsent) unsubscribeFromConsent();
  unsubscribeFromConsent = null;
  lastPushedSignals = null;
}
